// src/hooks/useUniverse.js
//
// Fetches predefined stock universe lists (S&P 500, NASDAQ-100, Russell 1000).
// Lists are cached server-side in Redis (24h TTL), so this is cheap.
//
// Usage:
//   const { universe, loading, error } = useUniverse('sp500');
//   // universe is an array of { symbol, name, sector? }
//
// Supported indexes (must match backend's universeIndex.js):
//   'sp500'        — S&P 500
//   'nasdaq100'    — NASDAQ-100
//   'russell1000'  — Russell 1000
//
// Pass null/undefined to disable (returns empty array, no fetch).

import { useEffect, useState } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASEURL;


// Module-level cache so multiple components share fetches
const _moduleCache = new Map();   // index → { data, fetchedAt, promise }
const TTL_MS = 60 * 60 * 1000;     // 1 hour on the client


export function useUniverse(indexName) {
  const [state, setState] = useState({
    universe: [],
    loading: !!indexName,
    error: null,
  });

  useEffect(() => {
    if (!indexName) {
      setState({ universe: [], loading: false, error: null });
      return;
    }

    let cancelled = false;
    const now = Date.now();
    const cached = _moduleCache.get(indexName);

    // Cache hit — return immediately
    if (cached?.data && now - cached.fetchedAt < TTL_MS) {
      setState({ universe: cached.data, loading: false, error: null });
      return;
    }

    // In-flight — piggyback on existing promise
    if (cached?.promise) {
      cached.promise.then(
        (data) => { if (!cancelled) setState({ universe: data, loading: false, error: null }); },
        (err) => { if (!cancelled) setState({ universe: [], loading: false, error: err.message }); }
      );
      return () => { cancelled = true; };
    }

    // Fresh fetch
    setState({ universe: [], loading: true, error: null });

    const promise = axios
      .get(`${API_BASE}/api/universe/${encodeURIComponent(indexName)}`, {
        headers: { Authorization: localStorage.getItem('token') },
        timeout: 15000,
      })
      .then(resp => {
        const data = Array.isArray(resp.data?.symbols) ? resp.data.symbols : [];
        _moduleCache.set(indexName, { data, fetchedAt: Date.now(), promise: null });
        if (!cancelled) setState({ universe: data, loading: false, error: null });
        return data;
      })
      .catch(err => {
        const msg = err?.response?.data?.error || err?.message || 'Failed to load universe';
        _moduleCache.set(indexName, { data: null, fetchedAt: 0, promise: null });
        if (!cancelled) setState({ universe: [], loading: false, error: msg });
        throw err;
      });

    _moduleCache.set(indexName, { data: null, fetchedAt: 0, promise });

    return () => { cancelled = true; };
  }, [indexName]);

  return state;
}


// List of supported universes — used to render the dropdown options
export const UNIVERSE_OPTIONS = [
  { value: 'sp500',       label: 'S&P 500',      description: '500 large-cap US stocks' },
  { value: 'nasdaq100',   label: 'NASDAQ-100',   description: '100 largest non-financial NASDAQ stocks' },
  { value: 'russell1000', label: 'Russell 1000', description: '1,000 largest US public companies' },
  { value: 'custom',      label: 'Custom list',  description: 'Paste your own comma-separated symbols' },
];
